import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ScienceContent } from "@/components/ScienceContent";
import { Loader2, Plus, Search, Copy, Archive, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { question: "", answer: "", explanation: "", tags: "", spec_ref: "", published: true };

export default function Flashcards() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ course_id: "", module_id: "", topic_id: "", deck_id: "", published: "", q: "" });
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: coursesData } = useQuery({ queryKey: ["admin-courses"], queryFn: async () => (await api.get("/admin/courses")).data });
  const { data: modulesData } = useQuery({ queryKey: ["admin-modules", filters.course_id], enabled: !!filters.course_id, queryFn: async () => (await api.get(`/admin/modules?course_id=${filters.course_id}`)).data });
  const { data: topicsData } = useQuery({ queryKey: ["admin-topics", filters.module_id], enabled: !!filters.module_id, queryFn: async () => (await api.get(`/admin/topics?module_id=${filters.module_id}`)).data });
  const { data: decksData } = useQuery({ queryKey: ["admin-decks", filters.topic_id], enabled: !!filters.topic_id, queryFn: async () => (await api.get(`/admin/decks?topic_id=${filters.topic_id}`)).data });

  const query = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
  const { data, isLoading } = useQuery({ queryKey: ["admin-cards", query], queryFn: async () => (await api.get(`/admin/cards?${query}`)).data });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-cards"] });

  const doArchive = async () => {
    await api.post(`/admin/cards/${archiveTarget.id}/archive`);
    toast.success("Card archived");
    setArchiveTarget(null); refresh();
  };
  const duplicate = async (c) => { await api.post(`/admin/cards/${c.id}/duplicate`); toast.success("Duplicated"); refresh(); };
  const togglePublish = async (c) => { await api.post(`/admin/cards/${c.id}/publish?publish=${!c.published}`); refresh(); };
  const doDelete = async () => {
    try {
      await api.delete(`/admin/cards/${deleteTarget.id}`);
      toast.success("Card deleted");
    } catch (e) { toast.error(apiErr(e)); }
    setDeleteTarget(null); refresh();
  };

  const courses = coursesData?.courses ?? [];
  const canAdd = filters.course_id && filters.module_id && filters.topic_id && filters.deck_id;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-slate-400">Admin</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Flashcards</h1>
        </div>
        <Button disabled={!canAdd} onClick={() => setEditing({ ...empty })} data-testid="add-card-button" className="bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-2" /> Add card
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
        <FilterSelect label="Course" value={filters.course_id} items={courses} onChange={(v) => setFilters({ ...filters, course_id: v, module_id: "", topic_id: "", deck_id: "" })} testid="filter-course" />
        <FilterSelect label="Module" value={filters.module_id} items={modulesData?.modules} disabled={!filters.course_id} onChange={(v) => setFilters({ ...filters, module_id: v, topic_id: "", deck_id: "" })} testid="filter-module" />
        <FilterSelect label="Topic" value={filters.topic_id} items={topicsData?.topics} disabled={!filters.module_id} onChange={(v) => setFilters({ ...filters, topic_id: v, deck_id: "" })} testid="filter-topic" />
        <FilterSelect label="Deck" value={filters.deck_id} items={decksData?.decks} disabled={!filters.topic_id} onChange={(v) => setFilters({ ...filters, deck_id: v })} testid="filter-deck" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search…" className="pl-9 bg-white" data-testid="card-search-input" />
        </div>
      </div>
      {!canAdd && <p className="text-xs text-slate-400">Select a course, module, topic and deck to add a card.</p>}

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {isLoading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : data.cards.length === 0 ? (
          <div className="py-16 text-center text-slate-500">No cards match. Adjust filters or add a card.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.cards.map((c) => (
              <div key={c.id} className="p-4 flex items-start justify-between gap-4" data-testid={`card-row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={c.published ? "default" : "secondary"} className={c.published ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : ""}>{c.published ? "Published" : "Draft"}</Badge>
                    {!c.active && <Badge variant="secondary">Archived</Badge>}
                    {c.spec_ref && <span className="text-xs font-mono text-slate-400">{c.spec_ref}</span>}
                  </div>
                  <div className="text-slate-900 line-clamp-2 text-sm">{c.question}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setPreview(c)} data-testid={`preview-card-${c.id}`}><Eye className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(c)} data-testid={`edit-card-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => duplicate(c)} data-testid={`duplicate-card-${c.id}`}><Copy className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => togglePublish(c)} data-testid={`publish-card-${c.id}`} className="text-xs">{c.published ? "Unpublish" : "Publish"}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setArchiveTarget(c)} data-testid={`archive-card-${c.id}`}><Archive className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(c)} data-testid={`delete-card-${c.id}`} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {data && <p className="text-xs text-slate-400">{data.total} cards</p>}

      <CardEditor editing={editing} onClose={() => setEditing(null)} filters={filters} onSaved={() => { setEditing(null); refresh(); }} />

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="card-preview-dialog">
          <DialogHeader><DialogTitle>Card preview</DialogTitle></DialogHeader>
          <div className="rounded-lg border border-slate-200 p-4"><div className="text-xs text-slate-400 mb-1">Question</div><ScienceContent text={preview?.question} images={preview?.images} protect={false} /></div>
          <div className="rounded-lg border border-slate-200 p-4 bg-slate-50"><div className="text-xs text-slate-400 mb-1">Answer</div><ScienceContent text={preview?.answer} protect={false} /></div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Archive this card?</AlertDialogTitle>
            <AlertDialogDescription>It will be unpublished and hidden from students. Student learning history is preserved.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doArchive} data-testid="confirm-archive">Archive</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this card permanently?</AlertDialogTitle>
            <AlertDialogDescription>This removes the card and all student progress and review history for it. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete} data-testid="confirm-delete-card" className="bg-rose-600 hover:bg-rose-700">Delete permanently</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FilterSelect({ label, value, items, onChange, disabled, testid }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)} disabled={disabled}>
      <SelectTrigger className="bg-white" data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all">All {label.toLowerCase()}s</SelectItem>
        {items?.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function CardEditor({ editing, onClose, filters, onSaved }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [resetProgress, setResetProgress] = useState(false);
  const isEdit = editing && editing.id;

  useEffect(() => {
    if (!editing) return;
    setForm({
      question: editing.question || "", answer: editing.answer || "", explanation: editing.explanation || "",
      tags: Array.isArray(editing.tags) ? editing.tags.join(", ") : (editing.tags || ""),
      spec_ref: editing.spec_ref || "", published: editing.published ?? true,
    });
    setResetProgress(false);
  }, [editing]);

  const save = async () => {
    setBusy(true);
    try {
      const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      if (isEdit) {
        await api.patch(`/admin/cards/${editing.id}`, {
          question: form.question, answer: form.answer, explanation: form.explanation,
          tags, spec_ref: form.spec_ref, published: form.published, reset_progress: resetProgress,
        });
      } else {
        await api.post("/admin/cards", {
          course_id: filters.course_id, module_id: filters.module_id, topic_id: filters.topic_id, deck_id: filters.deck_id,
          question: form.question, answer: form.answer, explanation: form.explanation,
          tags, spec_ref: form.spec_ref, published: form.published,
        });
      }
      toast.success(isEdit ? "Card updated" : "Card created");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="card-editor-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit card" : "New card"}</DialogTitle>
          <DialogDescription>Supports Markdown and LaTeX. Use $...$ for maths, and _ / ^ or HTML sub/sup for formulae like H₂O.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div><Label>Question</Label><Textarea rows={4} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="card-question-input" className="mt-1.5 font-mono text-sm" /></div>
            <div><Label>Answer</Label><Textarea rows={5} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="card-answer-input" className="mt-1.5 font-mono text-sm" /></div>
            <div><Label>Explanation (optional)</Label><Textarea rows={2} value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} className="mt-1.5 font-mono text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tags (comma-sep)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Spec reference</Label><Input value={form.spec_ref} onChange={(e) => setForm({ ...form, spec_ref: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="card-published-switch" /><Label>Published</Label></div>
            {isEdit && (
              <label className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <Checkbox checked={resetProgress} onCheckedChange={setResetProgress} data-testid="reset-progress-checkbox" className="mt-0.5" />
                <span>Reset student scheduling for this card (only for major meaning changes — this cannot be undone).</span>
              </label>
            )}
          </div>
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-xs text-slate-400 mb-1">Question preview</div>
              <ScienceContent text={form.question} protect={false} />
            </div>
            <div className="rounded-lg border border-slate-200 p-4 bg-slate-50">
              <div className="text-xs text-slate-400 mb-1">Answer preview</div>
              <ScienceContent text={form.answer} protect={false} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !form.question || !form.answer} data-testid="save-card-button" className="bg-slate-900 hover:bg-slate-800">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save card"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
